// Lib
import React from 'react';
import classNames from 'classnames';

// Components
import ElementImage from '../../../../../components/images/ElementImage';
import Caption from '../../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../../drawing/DrawingSvg';

// Utils
import { getCaption, getShowCaption } from '../../../../../../common/elements/utils/elementPropertyUtils';

// Constants
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';

// Types
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import type { useBackgroundRemovalWipeOverlay } from '../../content/hooks/useBackgroundRemovalWipeOverlay';
import type { getImageModalEditorDrawingViewBox } from '../../content/utils/imageModalEditorDrawingUtils';

type WipeOverlay = ReturnType<typeof useBackgroundRemovalWipeOverlay>['wipeOverlay'];
type DrawingViewBox = ReturnType<typeof getImageModalEditorDrawingViewBox>;

export type ImageModalV2EditorSurfaceProps = {
    element: ImMNElement;
    editorContentRef?: React.RefObject<HTMLDivElement>;
    imageRef: React.MutableRefObject<HTMLImageElement | null>;
    isAwaitingPreviewPaint?: boolean;
    showEditorLoadingPreview: boolean;
    isCropToolSelected: boolean;
    isCropSourceSwapPending: boolean;
    displayModalWidth: number;
    displayModalHeight: number;
    isRotatedOnSide: boolean;
    // The session's starting rotate/flip, applied to the loading preview so it matches the editor.
    loadingImageTransform: string;
    useSecureMediaUrl: boolean;
    displayElement: ImMNElement;
    cropSourceImage: MNImageContent;
    elementId: string;
    largestUneditedImageUrl?: string | null;
    drawingPreviewElement: ImMNElement;
    drawingViewBox: DrawingViewBox;
    hasDrawing: boolean;
    editorIsReady: boolean;
    showImageReplacementWipe: boolean;
    wipeOverlay: WipeOverlay;
    onWipeAnimationEnd: () => void;
};

/**
 * Presentational surface for the V2 image editor: the loading preview, the Cropper source image, the
 * background-removal reveal wipe, the read-only drawing overlay, and the caption, laid out over the pinned
 * editor footprint. All state and choreography lives in ImageModalV2EditorContent; this component only
 * renders the frame it is handed.
 */
const ImageModalV2EditorSurface: React.FC<ImageModalV2EditorSurfaceProps> = ({
    element,
    editorContentRef,
    imageRef,
    isAwaitingPreviewPaint,
    showEditorLoadingPreview,
    isCropToolSelected,
    isCropSourceSwapPending,
    displayModalWidth,
    displayModalHeight,
    isRotatedOnSide,
    loadingImageTransform,
    useSecureMediaUrl,
    displayElement,
    cropSourceImage,
    elementId,
    largestUneditedImageUrl,
    drawingPreviewElement,
    drawingViewBox,
    hasDrawing,
    editorIsReady,
    showImageReplacementWipe,
    wipeOverlay,
    onWipeAnimationEnd,
}) => (
    <div
        ref={editorContentRef}
        className={classNames('image-modal-content', 'image-modal-content-v2', {
            'is-awaiting-preview-paint': isAwaitingPreviewPaint,
        })}
    >
        <div className="image-container">
            <div className="ImageEditor ImageEditor-v2">
                <div
                    className={classNames('editor', 'editor-v2', {
                        loading: showEditorLoadingPreview,
                        'crop-tool-disabled': !isCropToolSelected || isCropSourceSwapPending,
                    })}
                    style={{ width: displayModalWidth, height: displayModalHeight }}
                >
                    {showEditorLoadingPreview && (
                        <div className="loading-message">
                            <div
                                className="loading-image"
                                style={{
                                    transform: loadingImageTransform,
                                    width: isRotatedOnSide ? displayModalHeight : displayModalWidth,
                                    height: isRotatedOnSide ? displayModalWidth : displayModalHeight,
                                }}
                            >
                                <ElementImage
                                    useSecureMediaUrl={useSecureMediaUrl}
                                    showBrokenIconOnError
                                    element={displayElement}
                                    imageData={cropSourceImage}
                                    imageType={IMAGE_TYPES.ELEMENT}
                                    widthPx={displayModalWidth}
                                    key={`${elementId}-${largestUneditedImageUrl}`}
                                />
                            </div>
                            <SimpleDrawingSvg element={drawingPreviewElement} viewBox={drawingViewBox} />
                        </div>
                    )}

                    <img
                        ref={(c) => {
                            imageRef.current = c;
                        }}
                        className="editor-source-image"
                        // Never shown: the loading preview covers the initial load and Cropper displays its
                        // own cloned img. Cropper.replace() un-hides this element (incoming src, no transform)
                        // while it rebuilds, so it must stay invisible on its own.
                        style={{ width: displayModalWidth, height: displayModalHeight, visibility: 'hidden' }}
                    />
                    {wipeOverlay && (
                        // The clip-path wipe lives on the outer element (screen space) so it always
                        // sweeps right-to-left, while the flip/rotate transform is applied to the inner
                        // image layer only — otherwise a horizontal flip would also mirror the wipe.
                        <div
                            key={wipeOverlay.renderKey}
                            className={classNames('bg-removal-wipe-overlay', {
                                'is-wiping': wipeOverlay.wiping,
                            })}
                            onAnimationEnd={onWipeAnimationEnd}
                            aria-hidden
                        >
                            <div
                                className="bg-removal-wipe-overlay-image"
                                style={{
                                    // Size to the un-rotated footprint (swapping w/h when rotated onto
                                    // its side) before the rotate transform is applied, exactly like
                                    // loadingImageStyles. Otherwise the outgoing frame is fitted into the
                                    // rotated footprint and then rotated again, leaving the stale image at
                                    // a different scale/offset to the cropper underneath — two mismatched
                                    // images during the wipe.
                                    width: isRotatedOnSide ? displayModalHeight : displayModalWidth,
                                    height: isRotatedOnSide ? displayModalWidth : displayModalHeight,
                                    backgroundImage: `url(${wipeOverlay.dataUrl})`,
                                    transform: wipeOverlay.transform,
                                }}
                            />
                        </div>
                    )}
                    {hasDrawing && (editorIsReady || showImageReplacementWipe) && (
                        <div className="drawing-svg-container">
                            <div className="drawing-wrapper">
                                <SimpleDrawingSvg element={drawingPreviewElement} viewBox={drawingViewBox} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Caption element={element} textContent={getCaption(element)} captionVisible={getShowCaption(element)} />
        </div>
    </div>
);

export default ImageModalV2EditorSurface;
